<template>
  <div>
    <span class="task-edition__text">Дата создания: {{ initialTask.createdAt }}</span>
    <form v-on:submit="onEdit" class="task-edition">
      <input-text-length-limitation
        class="task-creation"
        title="Заголовок"
        v-bind:max-length="200"
        v-bind:violations="getViolationsBySource('task.title')"
        v-model="title"
      />
      <autocomplete-input
        class="task-creation"
        clearable
        label="Исполнитель"
        v-model="performerId"
        item-text="title"
        item-value="value"
        required="required"
        v-bind:items="performers"
        v-bind:violations="getViolationsBySource('task.performer')"
      />
      <autocomplete-input
        attach
        class="appointment-creation__patient multiline"
        hide-details
        item-text="title"
        item-value="value"
        label="ФИО пациента"
        required="required"
        v-bind:clearable="true"
        v-bind:violations="getViolationsBySource('task.patient')"
        v-bind:items="patients"
        v-bind:search-input.sync="patientSearchQuery"
        v-bind:value="patientId"
        v-on:blur="onBlurInputPatient"
        v-on:input="onInputPatient"
      />
      <div class="task-edition__data">
        <div class="task-edition__data__label">Дедлайн</div>
        <date-picker-dialog
          v-bind:allowed-dates="allowedDates"
          v-bind:value="deadLineDate"
          v-on:input="setNewDate"
        >
          <template v-slot:trigger="{ listenerObject }">
            <button
              title="Сменить дату"
              v-if="deadLineDate"
              type="button"
              v-on="listenerObject"
            >
              {{ deadLineTitle }}
            </button>
          </template>
        </date-picker-dialog>
      </div>
      <div class="task-edition__status">
        <div>Статус:</div>
        <the-select
          v-model="status"
          v-bind:options="$options.statuses"
          v-bind:violations="getViolationsBySource('task.status')"
        />
      </div>
      <textarea-length-limitation
        placeholder="Подробное описание"
        title="Подробное описание"
        v-model="description"
        v-bind:max-length="300"
        v-bind:violations="getViolationsBySource('task.description')"
      />
      <button class="task-edition__button" type="button" v-on:click="onEdit">Сохранить</button>
    </form>
  </div>
</template>

<script>

import DatePickerDialog from '@/components/DatePickerDialog.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import TheSelect from '@/components/TheSelect.vue';
import { TaskRequest, statuses, TaskForEdit } from '@/storage/gateways/TaskGateway';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import debounce from 'lodash.debounce';
import InputTextLengthLimitation from '@/components/InputTextLengthLimitation.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';

export default {
  name: 'FormTaskEditing',
  components: {
    AutocompleteInput,
    InputTextLengthLimitation,
    TheSelect,
    TextareaLengthLimitation,
    DatePickerDialog,
  },
  data() {
    const deadLine = this.initialTask.deadLine.split('.').reverse().join('-');
    const currentDate = new Date(getISODateStringByDate(new Date()));
    return {
      /**
       * @description Запрос на поиск пациента.
       * @type {string}
       */
      patientSearchQuery: '',
      currentDate,
      deadLine,
      deadLineDate: new Date(deadLine),
      description: this.initialTask.description,
      id: this.initialTask.id,
      performerId: this.initialTask.performerId,
      status: this.initialTask.status,
      title: this.initialTask.title,
      patientId: this.initialTask.patientId,
    };
  },
  methods: {
    /**
     * @description Обработать изменение пациента.
     * @param {?number} value
     * @returns {void}
     */
    onInputPatient(value) {
      this.patientId = value;
      if (value && this.isPatientFieldInvalid) {
        this.isPatientFieldInvalid = false;
      }
    },
    /**
     * @description Обработать потерю фокуса с поля выбора пациента.
     * @returns {void}
     */
    onBlurInputPatient() {
      this.isPatientFieldInvalid = !this.patientId && this.patientSearchQuery;
    },
    /**
     * @description Функция описывающая условие доступности выбора дат в datepicker
     * @param {String} candidate Строка с датой в формате Y-m-d
     * @return {boolean}
     */
    allowedDates(candidate) {
      return (new Date(candidate)) >= this.currentDate;
    },
    /**
     * @description Инициализировать событие поднятие статуса
     * @param val
     */
    dispatchTaskIsChanged(val) {
      this.$emit('task-is-changed', val);
    },
    /**
     * @description Отобразить причины отказа в исполнении запроса
     * @param source
     * @return {*}
     */
    getViolationsBySource(source) {
      return this.violations?.[source] || [];
    },
    /**
     * @description Событие запроса на редактирование
     */
    onEdit() {
      const request = new TaskRequest(
        this.title,
        this.performerId,
        this.deadLine,
        this.status,
        this.description,
        this.patientId,
      );
      this.$emit('edit-task', request, this.id);
    },
    /**
     * @description Установить новую дату
     * @param {Date} date
     */
    setNewDate(date) {
      this.deadLine = getISODateStringByDate(date);
      this.deadLineDate = date;
    },
  },
  computed: {
    /**
     * @description Состояние не соответствует начальному
     * @return {boolean}
     */
    taskIsChanged() {
      const initialDeadLine = this.initialTask.deadLine.split('.').reverse().join('-');
      return !(this.deadLine === initialDeadLine
        && this.description === this.initialTask.description
        && this.performerId === this.initialTask.performerId
        && this.status === this.initialTask.status
        && this.title === this.initialTask.title
        && this.patientId === this.initialTask.patientId);
    },
    /**
     * @description Заголовок крайнего срока
     * @return {string}
     */
    deadLineTitle() {
      const options = {
        day: 'numeric',
        month: 'long',
      };
      const weekday = this.deadLineDate.toLocaleDateString('ru-RU', { weekday: 'short' });
      return `${this.deadLineDate.toLocaleDateString('ru-RU', options)}, ${weekday.toUpperCase()}`;
    },
  },
  watch: {
    taskIsChanged: {
      handler: debounce(function _(val) {
        this.dispatchTaskIsChanged(val);
      }, 300),
      deep: true,
    },
  },
  props: {
    performers: { type: Array, default: () => [] },
    patients: { type: Array, default: () => [] },
    initialTask: { type: TaskForEdit, required: true },
    violations: { type: Object, default: () => {} },
  },
  statuses,
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.task-edition {
  display: flex;
  flex-direction: column;
  gap: 29px;
  margin: 27px 0 0 0;
  &__text {
    display: flex;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #192A3E;
    margin: 5px 0 0 0;
  }
  &__data {
    &__label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: #90A0B7;
      margin: 0 0 3px 0;
    }
    button {
      padding: 10px 50px 10px 10px;
      border: 1px solid #C2CFE0;
      border-radius: 4px;
      font-size: 16px;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: right 10px center;
    }
  }
  &__status {
    display: grid;
    grid-template-columns: 55px 196px;
    align-items: center;
    grid-gap: 22px;
    div {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #192A3E;
    }
  }
  &__button {
    @include buttons.btn-blue(215px, 56px, 20px);
    margin: 100px 0 0 auto;
  }
}
</style>
